import React, { useState } from 'react';
import { FileText, BookOpen, HelpCircle, Shield, Wrench, Database, Copy, CheckCircle, ChevronRight } from 'lucide-react';
import Icon from '../../../components/AppIcon';


export default function TemplatesPanel({ onUseTemplate, onClose }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [copiedTemplate, setCopiedTemplate] = useState(null);

  const templates = {
    guide: {
      icon: BookOpen,
      color: 'blue',
      title: 'Guide Template',
      description: 'Step-by-step instructional content for completing specific tasks',
      targetRoles: ['All roles'],
      structure: `# [Guide Title]

## Overview
Brief introduction explaining what this guide covers and who should use it.

**Intended Audience:** [Specify role level]
**Estimated Time:** [X minutes]
**Prerequisites:** [List any requirements]

## What You'll Learn
- Key learning point 1
- Key learning point 2
- Key learning point 3

---

## Step 1: [First Major Step]
Clear, actionable instruction for the first step.

**To complete this step:**
1. Sub-action with specific details
2. Sub-action with expected outcome
3. Sub-action with verification method

💡 **Tip:** Helpful context or best practice

⚠️ **Important:** Critical information to prevent errors

---

## Step 2: [Second Major Step]
[Continue pattern for each major step]

---

## Common Issues & Solutions

### Issue: [Problem Description]
**Solution:** Step-by-step resolution

### Issue: [Problem Description]
**Solution:** Step-by-step resolution

---

## Next Steps
- What to do after completing this guide
- Related guides or resources
- Advanced topics to explore

## Need Help?
Contact [support channel] for assistance.`,
      contentGuidelines: [
        'Use action-oriented headings (e.g., "Create Invoice" not "Invoice Creation")','Include screenshots for visual guidance (describe clearly for accessibility)','Write in second person ("You will..." not "Users will...")','Break complex procedures into 3-7 main steps maximum','Include expected outcomes for each step','Add tips and warnings using callout formatting'
      ]
    },
    tutorial: {
      icon: FileText,
      color: 'green',title: 'Tutorial Template',description: 'In-depth educational content teaching concepts and best practices',
      targetRoles: ['All roles'],
      structure: `# [Tutorial Title]: [Main Learning Objective]

## Introduction

### What You'll Build/Learn
Specific, measurable outcome of this tutorial.

### Why This Matters
Real-world business value and application.

### Prerequisites
- Required knowledge or skills
- Tools or access needed
- Estimated completion time: [X minutes]

---

## Concepts Overview

### Key Concept 1: [Concept Name]
Explanation of the concept with business context.

### Key Concept 2: [Concept Name]
Explanation with examples from the CRM system.

---

## Hands-On Exercise

### Part 1: [Building Block Name]
**Learning Goal:** What this section teaches

**Steps:**
1. Detailed instruction with explanation of WHY
2. Expected result and what it means
3. How this connects to the larger picture

**Check Your Understanding:**
- Question or task to verify comprehension

### Part 2: [Building Block Name]
[Continue pattern for each learning segment]

---

## Best Practices
✅ **Do:** Recommended approach with rationale
✅ **Do:** Another best practice
❌ **Don't:** Anti-pattern to avoid with explanation

---

## Real-World Application

### Example Scenario
Practical business case showing tutorial concepts in action.

### Your Turn
Practice exercise for learners to apply knowledge.

---

## Summary
- Key takeaway 1
- Key takeaway 2
- Key takeaway 3

## Continue Learning
- Related tutorials
- Advanced topics
- Community resources`,
      contentGuidelines: [
        'Focus on teaching concepts, not just procedures','Include "why" explanations, not just "how"','Provide context for real-world application','Build complexity gradually (beginner → advanced)','Include practice exercises or checkpoints','Use analogies to explain complex concepts','Link to related learning resources'
      ]
    },
    faq: {
      icon: HelpCircle,
      color: 'yellow',title: 'FAQ Template',description: 'Quick answers to common questions organized by topic',
      targetRoles: ['All roles'],
      structure: `# [Feature/Topic] FAQ

## Quick Navigation
- [General Questions](#general)
- [Getting Started](#getting-started)
- [Common Tasks](#common-tasks)
- [Troubleshooting](#troubleshooting)
- [Advanced Topics](#advanced)

---

## General Questions {#general}

### What is [Feature Name]?
Clear, concise answer explaining the feature's purpose and value.

**Related Questions:**
- [Link to related FAQ]
- [Link to detailed guide]

---

### Who can use [Feature Name]?
Role-specific access information with explanation.

**Permissions Required:**
- Permission 1
- Permission 2

[Learn more about permissions →](#)

---

## Getting Started {#getting-started}

### How do I access [Feature]?
Step-by-step navigation with screenshots described.

### What do I need before I start?
Prerequisites checklist.

---

## Common Tasks {#common-tasks}

### How do I [common action]?
Quick answer with steps.

**For detailed instructions:** [Link to full guide]

### Can I [specific action]?
Yes/No with explanation and any limitations.

---

## Troubleshooting {#troubleshooting}

### Why am I seeing [error/issue]?
**Cause:** Explanation of root cause
**Solution:** Fix with step-by-step instructions
**Prevention:** How to avoid this in the future

---

## Advanced Topics {#advanced}

### How do I [advanced feature]?
Brief answer with link to advanced documentation.

### What are the limitations of [feature]?
Transparent explanation of constraints and workarounds.

---

## Still Have Questions?
- Search the Knowledge Base
- Contact Support: [contact method]
- Request a Feature: [process]`,
      contentGuidelines: [
        'Write questions in user\'s language (how they would ask)',
        'Keep answers concise (2-3 paragraphs max)',
        'Link to detailed guides for complex topics',
        'Group related questions logically',
        'Use clear, jargon-free language',
        'Update frequently based on support tickets',
        'Include "last updated" date at top'
      ]
    },
    policy: {
      icon: Shield,
      color: 'red',
      title: 'Policy Template',
      description: 'Official guidelines, rules, and compliance requirements',
      targetRoles: ['Super Admin', 'Business Admin', 'Manager'],
      structure: `# [Policy Name]

**Policy Number:** [XXX]
**Effective Date:** [Date]
**Last Reviewed:** [Date]
**Approved By:** [Authority]
**Applies To:** [Roles/Users]

---

## Purpose
Why this policy exists and what it aims to achieve.

---

## Scope
Who this policy applies to and in what situations.

---

## Policy Statement

### [Major Requirement 1]
Clear statement of the rule or requirement.

**Rationale:** Why this requirement exists
**Compliance:** What users must do

### [Major Requirement 2]
[Continue pattern for each policy section]

---

## Roles & Responsibilities

### [Role Name]
- Specific responsibility 1
- Specific responsibility 2

### [Role Name]
- Specific responsibility 1
- Specific responsibility 2

---

## Procedures

### How to Comply
1. Specific action users must take
2. Required documentation or approvals
3. Frequency or timing requirements

### Exceptions Process
When and how exceptions can be requested.

---

## Consequences of Non-Compliance
- First violation: [consequence]
- Repeated violations: [escalation]
- Severe violations: [serious consequences]

---

## Related Policies
- [Link to related policy]
- [Link to supporting documentation]

---

## Questions or Clarifications
Contact: [Policy Owner]
Email: [contact]
Department: [responsible team]

---

## Revision History
| Date | Version | Changes | Approved By |
|------|---------|---------|-------------|
| [Date] | 1.0 | Initial policy | [Name] |`,
      contentGuidelines: [
        'Use formal, authoritative tone',
        'Be specific and unambiguous',
        'Include legal/compliance references where applicable',
        'Clearly state who, what, when, where, why',
        'Version control is mandatory',
        'Review and update regularly (at least annually)',
        'Ensure accessibility for all affected users',
        'Include approval signatures/dates'
      ]
    },
    troubleshooting: {
      icon: Wrench,
      color: 'orange',
      title: 'Troubleshooting Template',
      description: 'Diagnostic guides for resolving specific issues',
      targetRoles: ['All roles'],
      structure: `# Troubleshooting: [Problem Category]

## Problem Summary
Brief description of the issue users are experiencing.

**Symptoms:**
- Observable behavior 1
- Observable behavior 2
- Observable behavior 3

**Impact:** Who is affected and how critical the issue is.

---

## Quick Fixes (Try These First)

### Fix 1: [Simple Solution]
**When this works:** Scenario description
**Steps:**
1. Action to take
2. Expected result

✅ **Solved?** [Link to verification section]
❌ **Still broken?** Try Fix 2 →

### Fix 2: [Another Common Solution]
[Continue pattern]

---

## Diagnostic Steps

### Step 1: Verify [Basic Requirement]
**Check if:**
- Condition 1 is met
- Condition 2 is true

**How to verify:**
[Specific instructions with screenshots]

**Results:**
- ✅ **If YES:** Proceed to Step 2
- ❌ **If NO:** [Specific action to resolve]

### Step 2: Check [Next Component]
[Continue systematic diagnostic approach]

---

## Common Causes & Solutions

### Cause 1: [Specific Issue]
**How to identify:**
Signs that this is the problem.

**Solution:**
1. Detailed fix steps
2. Verification method
3. Prevention tips

### Cause 2: [Specific Issue]
[Continue pattern for each cause]

---

## Advanced Troubleshooting

⚠️ **Admin-Only Actions**
These steps require [specific permission level].

### Advanced Fix 1
[Detailed technical steps]

---

## Still Not Working?

### Before Contacting Support
Gather this information:
- [ ] Error message (exact text)
- [ ] Screenshot of the issue
- [ ] Steps you've already tried
- [ ] When the problem started
- [ ] Your role and permissions

### Get Help
- **Quick Help:** [Link to related FAQs]
- **Live Support:** [Contact method]
- **Submit Ticket:** [Support system]

---

## Prevention Tips
How to avoid this issue in the future:
- Preventive action 1
- Preventive action 2
- Best practice 3`,
      contentGuidelines: [
        'Start with quickest/most common solutions','Use clear problem/solution pairing','Include decision trees or flowcharts when helpful','Specify exact error messages to search for','Indicate required permission levels','Provide information gathering checklist','Link to related troubleshooting guides','Update based on resolved support tickets'
      ]
    },
    reference: {
      icon: Database,
      color: 'purple',title: 'Reference Template',description: 'Technical specifications, field definitions, and lookup information',
      targetRoles: ['Super Admin', 'Business Admin', 'Manager'],
      structure: `# [Feature/Component] Reference

**Last Updated:** [Date]
**Applies To:** [System version or module]

---

## Overview
Brief explanation of what this reference covers and when to use it.

**Use this reference when:**
- Scenario 1
- Scenario 2
- Scenario 3

---

## Quick Reference Table

| Item | Description | Values/Options | Default |
|------|-------------|----------------|---------|
| [Field 1] | What it means | Valid options | Default value |
| [Field 2] | What it means | Valid options | Default value |

---

## Detailed Specifications

### [Component Name]

**Purpose:** What this component does

**Fields:**

#### Field Name 1
- **Type:** [Data type]
- **Required:** Yes/No
- **Format:** [Specific format requirements]
- **Valid Values:** [Acceptable inputs]
- **Default:** [Default value]
- **Validation:** [Rules and constraints]
- **Examples:**
  - Valid: \`example_value_1\`
  - Invalid: \`bad_example\` (reason why)

#### Field Name 2
[Continue pattern for each field]

---

## Permissions & Access

| Action | Required Permission | Role Level |
|--------|-------------------|------------|
| [Action 1] | [Permission] | [Minimum role] |
| [Action 2] | [Permission] | [Minimum role] |

---

## Business Rules

### Rule 1: [Rule Name]
**When:** Conditions that trigger this rule
**What Happens:** Specific behavior
**Example:** Concrete example scenario

### Rule 2: [Rule Name]
[Continue pattern]

---

## API Endpoints (if applicable)

### Endpoint: \`/api/[resource]\`
**Method:** GET/POST/PUT/DELETE
**Parameters:**
- \`param1\` (required): Description
- \`param2\` (optional): Description

**Response:**
\`\`\`json
{
  "field": "example_value"
}
\`\`\`

---

## Status Values & Workflows

### Status: [Status Name]
**Meaning:** What this status indicates
**Can transition to:** [Next statuses]
**Triggered by:** [Actions that set this status]

[Visual workflow diagram description]

---

## Related References
- [Link to related reference doc]
- [Link to API documentation]
- [Link to admin guide]

---

## Glossary
**Term:** Definition
**Term:** Definition`,
      contentGuidelines: [
        'Organize information for quick lookup','Use tables extensively for structured data','Include complete, accurate specifications','Provide concrete examples','Distinguish between "what" and "how" (reference vs. guide)','Keep technical jargon with clear definitions','Version control critical for API/field changes','Link to tutorials for learning, not just specs'
      ]
    }
  };

  const contentStandards = {
    tone: {
      title: 'Tone & Voice Standards',
      guidelines: [
        {
          category: 'Professional yet Approachable',
          do: [
            'Use "we" for the CRM system (collaborative)',
            'Use "you" to address the reader directly',
            'Keep sentences clear and conversational',
            'Use active voice ("Create an invoice" not "An invoice can be created")'
          ],
          dont: [
            'Use overly formal or academic language',
            'Use passive voice excessively',
            'Use jargon without explanation',
            'Talk down to users or assume ignorance'
          ]
        },
        {
          category: 'Role-Specific Adaptation',
          do: [
            'Customer articles: Explain business value, no technical details',
            'Staff articles: Focus on daily tasks, clear procedures',
            'Manager articles: Include team management context',
            'Admin articles: Provide technical depth and system impact'
          ],
          dont: [
            'Expose system architecture to customers',
            'Overwhelm non-technical users with jargon',
            'Oversimplify for technical audiences',
            'Mix role-level content in single articles'
          ]
        }
      ]
    },
    structure: {
      title: 'Article Structure Standards',
      guidelines: [
        {
          category: 'Organization',
          rules: [
            'Use descriptive, action-oriented headings',
            'Implement logical heading hierarchy (H1 → H2 → H3)',
            'Keep paragraphs short (3-4 sentences maximum)',
            'Use bulleted lists for 3+ related items',
            'Add "Quick Navigation" for articles over 5 sections'
          ]
        },
        {
          category: 'Visual Elements',
          rules: [
            'Include screenshots for UI-based instructions',
            'Describe all images with detailed alt text',
            'Use callout boxes for tips (💡), warnings (⚠️), important info (ℹ️)',
            'Add visual separators between major sections (---))',
            'Highlight code, field names, or UI elements with formatting'
          ]
        },
        {
          category: 'Content Flow',
          rules: [
            'Start with "What" and "Why" before "How"',
            'Progress from simple to complex concepts',
            'Provide context before detailed steps',
            'End with next steps or related resources',
            'Include "back to top" links for long articles'
          ]
        }
      ]
    },
    accessibility: {
      title: 'Accessibility Requirements',
      guidelines: [
        {
          category: 'WCAG 2.1 AA Compliance',
          requirements: [
            'Use semantic heading hierarchy (never skip levels)',
            'Provide descriptive alt text for ALL images',
            'Ensure color is not the only means of conveying information',
            'Use sufficient color contrast (4.5:1 for normal text)',
            'Make all interactive elements keyboard accessible'
          ]
        },
        {
          category: 'Screen Reader Optimization',
          requirements: [
            'Write descriptive link text (no "click here")',
            'Use ARIA labels for complex UI elements',
            'Provide text alternatives for charts/graphs',
            'Structure tables with proper headers',
            'Avoid ASCII art or complex formatting'
          ]
        },
        {
          category: 'Readability',
          requirements: [
            'Target 8th-9th grade reading level (Flesch-Kincaid)',
            'Keep sentences under 25 words when possible',
            'Define technical terms on first use',
            'Use short words over long ones',
            'Break up text with headings every 3-4 paragraphs'
          ]
        },
        {
          category: 'Internationalization Readiness',
          requirements: [
            'Avoid idioms and cultural references',
            'Use simple, direct language',
            'Specify date formats explicitly (YYYY-MM-DD)',
            'Note time zones when relevant',
            'Use universal symbols carefully (not all cultures read them the same)'
          ]
        }
      ]
    },
    roleMapping: {
      title: 'Role-Specific Content Guidelines',
      mapping: [
        {
          role: 'Customer',
          focus: 'Using portal features, viewing information, self-service',
          includeTopics: [
            'Viewing and paying invoices',
            'Booking appointments',
            'Updating contact information',
            'Accessing purchase history',
            'Understanding service offerings'
          ],
          excludeTopics: [
            'Internal CRM operations',
            'Admin configurations',
            'Team management',
            'System architecture',
            'Permission settings'
          ],
          toneNotes: 'Customer-friendly, benefit-focused, minimal technical details'
        },
        {
          role: 'Staff',
          focus: 'Daily operational tasks, data entry, customer interactions',
          includeTopics: [
            'Creating and managing contacts',
            'Processing invoices',
            'Scheduling appointments',
            'Using the calendar',
            'Recording customer interactions'
          ],
          excludeTopics: [
            'System administration',
            'Role and permission management',
            'Integration configuration',
            'Database operations',
            'Advanced reporting customization'
          ],
          toneNotes: 'Task-oriented, step-by-step procedures, practical examples'
        },
        {
          role: 'Manager',
          focus: 'Team oversight, reporting, workflow optimization',
          includeTopics: [
            'Team performance dashboards',
            'Approval workflows',
            'Report generation and analysis',
            'Managing team calendars',
            'Monitoring customer satisfaction',
            'Staff productivity insights'
          ],
          excludeTopics: [
            'Technical system configuration',
            'Database schema',
            'API integrations',
            'Security policy creation'
          ],
          toneNotes: 'Strategic context, management principles, team impact'
        },
        {
          role: 'Business Admin',
          focus: 'Company-wide configuration, business operations, compliance',
          includeTopics: [
            'Company settings and branding',
            'Service configuration',
            'Billing and subscription management',
            'Team structure and roles',
            'Business process workflows',
            'Compliance and policy enforcement'
          ],
          excludeTopics: [
            'Deep technical system administration',
            'Database direct access',
            'Server configuration'
          ],
          toneNotes: 'Business-focused, operational impact, policy context'
        },
        {
          role: 'Super Admin',
          focus: 'System administration, security, technical configuration',
          includeTopics: [
            'Role and permission management',
            'RLS policy configuration',
            'Integration setup and API access',
            'Security settings',
            'Audit trail analysis',
            'System troubleshooting',
            'Database operations',
            'Advanced customization'
          ],
          excludeTopics: [
            'Basic user tasks (covered in other roles)',
            'Customer-facing features'
          ],
          toneNotes: 'Technical depth, system architecture awareness, security implications'
        }
      ]
    }
  };

  const handleCopyTemplate = (templateKey) => {
    navigator.clipboard?.writeText(templates?.[templateKey]?.structure);
    setCopiedTemplate(templateKey);
    setTimeout(() => setCopiedTemplate(null), 2000);
  };

  const handleUseTemplate = (templateKey) => {
    onUseTemplate(templates?.[templateKey]?.structure);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full my-8 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Article Templates & Content Standards</h2>
          <p className="text-gray-600">Professional templates and guidelines for consistent, accessible documentation</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Templates Section */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="w-6 h-6 mr-2 text-indigo-600" />
                Article Templates
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(templates)?.map(([key, template]) => {
                  const Icon = template?.icon;
                  return (
                    <div
                      key={key}
                      className={`border-2 rounded-lg p-5 hover:border-${template?.color}-400 transition-all cursor-pointer ${
                        selectedTemplate === key ? `border-${template?.color}-500 bg-${template?.color}-50` : 'border-gray-200'
                      }`}
                      onClick={() => setSelectedTemplate(key)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center">
                          <Icon className={`w-8 h-8 text-${template?.color}-600 mr-3`} />
                          <div>
                            <h4 className="font-semibold text-gray-900">{template?.title}</h4>
                            <p className="text-xs text-gray-500 mt-1">{template?.description}</p>
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mb-3">
                        <strong>Target:</strong> {template?.targetRoles?.join(', ')}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleUseTemplate(key);
                          }}
                          className="flex-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                        >
                          Use Template
                        </button>
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleCopyTemplate(key);
                          }}
                          className={`px-3 py-2 text-sm rounded transition-colors ${
                            copiedTemplate === key
                              ? 'bg-green-100 text-green-700' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {copiedTemplate === key ? (
                            <>
                              <CheckCircle className="w-4 h-4 inline mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 inline mr-1" />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                      {selectedTemplate === key && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h5 className="font-medium text-gray-900 mb-2 text-sm">Content Guidelines:</h5>
                          <ul className="space-y-1">
                            {template?.contentGuidelines?.map((guideline, idx) => (
                              <li key={idx} className="text-xs text-gray-600 flex items-start">
                                <ChevronRight className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0 text-indigo-500" />
                                <span>{guideline}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Content Standards Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Shield className="w-6 h-6 mr-2 text-indigo-600" />
                Content Standards & Guidelines
              </h3>

              {/* Tone & Voice */}
              <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3">{contentStandards?.tone?.title}</h4>
                {contentStandards?.tone?.guidelines?.map((section, idx) => (
                  <div key={idx} className="mb-4 last:mb-0">
                    <h5 className="font-medium text-blue-800 mb-2 text-sm">{section?.category}</h5>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="bg-white rounded p-3 border border-green-200">
                        <p className="text-xs font-medium text-green-800 mb-2">✅ Do:</p>
                        <ul className="space-y-1">
                          {section?.do?.map((item, i) => (
                            <li key={i} className="text-xs text-gray-700">• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-white rounded p-3 border border-red-200">
                        <p className="text-xs font-medium text-red-800 mb-2">❌ Don't:</p>
                        <ul className="space-y-1">
                          {section?.dont?.map((item, i) => (
                            <li key={i} className="text-xs text-gray-700">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Structure Standards */}
              <div className="bg-purple-50 rounded-lg p-5 border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3">{contentStandards?.structure?.title}</h4>
                {contentStandards?.structure?.guidelines?.map((section, idx) => (
                  <div key={idx} className="mb-3 last:mb-0 bg-white rounded p-3 border border-purple-100">
                    <h5 className="font-medium text-purple-800 mb-2 text-sm">{section?.category}</h5>
                    <ul className="space-y-1">
                      {section?.rules?.map((rule, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <span className="text-purple-500 mr-2">▸</span>
                          <span>{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Accessibility Requirements */}
              <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                <h4 className="font-semibold text-green-900 mb-3">{contentStandards?.accessibility?.title}</h4>
                {contentStandards?.accessibility?.guidelines?.map((section, idx) => (
                  <div key={idx} className="mb-3 last:mb-0 bg-white rounded p-3 border border-green-100">
                    <h5 className="font-medium text-green-800 mb-2 text-sm">{section?.category}</h5>
                    <ul className="space-y-1">
                      {section?.requirements?.map((req, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Role Mapping */}
              <div className="bg-orange-50 rounded-lg p-5 border border-orange-200">
                <h4 className="font-semibold text-orange-900 mb-3">{contentStandards?.roleMapping?.title}</h4>
                <div className="space-y-3">
                  {contentStandards?.roleMapping?.mapping?.map((roleInfo, idx) => (
                    <div key={idx} className="bg-white rounded p-4 border border-orange-100">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-semibold text-orange-800">{roleInfo?.role}</h5>
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">{roleInfo?.focus}</span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3 mt-3">
                        <div>
                          <p className="text-xs font-medium text-green-700 mb-1">Include:</p>
                          <ul className="space-y-0.5">
                            {roleInfo?.includeTopics?.slice(0, 3)?.map((topic, i) => (
                              <li key={i} className="text-xs text-gray-600">• {topic}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-red-700 mb-1">Exclude:</p>
                          <ul className="space-y-0.5">
                            {roleInfo?.excludeTopics?.slice(0, 3)?.map((topic, i) => (
                              <li key={i} className="text-xs text-gray-600">• {topic}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2 italic">
                        <strong>Tone:</strong> {roleInfo?.toneNotes}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}